import React from 'react';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { useQuery } from '@apollo/client';
import { copyFor } from 'config';
import SolarPanels from '/images/solar/single-solar-panel.svg';
import { LoadingModule } from 'components/loading';
import {
  Grid,
  LeftColumnGridCell,
  RightColumnGridCell,
  MobileGridCell,
} from 'styles';
import { screenIsSmall } from 'utils/screen-is-small';
import { Welcome } from 'pages/solar/components/welcome';
import { PageHeading } from 'components/page-heading';
import {
  EnrollmentTimeline,
  EnrollmentStatus,
} from 'components/enrollment-timeline';
import { StateSolarFarms } from 'pages/solar/components/state-solar-farms';
import { AdvocacyModule } from 'pages/solar/components/advocacy-module';
import { SolarNotification } from 'pages/solar/components/solar-notification';
import { PersonalEnergyProduction } from 'pages/solar/components/personal-energy-production/personal-energy-production.component';
import { EnergyBreakdown } from 'pages/solar/components/energy-breakdown';
import { ProjectDetails } from 'pages/solar/components/project-details';
import { ReconnectUtilityNotification } from 'pages/solar/components/reconnect-utility-notification';
import { UserRewards } from 'pages/solar/components/user-rewards';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GET_USER_REWARD_STATUS } from 'pages/solar/components/user-rewards/user-rewards.api';
import { useSessionContext } from 'contexts/session';
import { SPONSORED_SOLAR } from 'pages/dashboard/dashboard.utils';
import { GET_PLAN_BILLING_INFO } from '../active.api';
import { showRewardsModule } from '../active.utils';

const getProjectDetailsCopy = copyFor('solar.projectDetails.pending');
const getPersonalEnergyProductionCopy = copyFor(
  'solar.personalEnergyProduction'
);

type LoadingProps = {
  header: React.ReactNode;
};

const Loading = ({ header }: LoadingProps) => (
  <>
    {header}
    <Grid data-testid="loading" width="100%" gap="24px">
      <LeftColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
      </LeftColumnGridCell>
      <RightColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
        <LoadingModule margin={{ top: '24px' }} />
      </RightColumnGridCell>
    </Grid>
  </>
);

export const Pending: React.FC<SolarActiveViewProps> = ({
  advocacyCampaign,
  completedAdvocacyCampaign,
  hasAdvocacyCampaignSubmission,
  pageHeading,
  stateAbbreviation,
  stateFullName,
  stateId,
  vnmSolarProjectsStatistics,
  zipCode,
  utilityAccountId,
  originationSourceType,
}) => {
  const { width } = useWindowSize();
  const { planId } = useParams<{ planId?: string }>();
  const billableResult = useQuery<
    GetPlanBillingInfoQuery,
    GetPlanBillingInfoQueryVariables
  >(GET_PLAN_BILLING_INFO, {
    skip: !planId,
    variables: { id: Number(planId) || -1 },
  });

  const { userId } = useSessionContext();
  const {
    data: userStatus,
    loading: userStatusLoading,
    error: userStatusError,
  } = useQuery(GET_USER_REWARD_STATUS, {
    variables: { userId },
  });

  const stateName = userStatus?.user?.zipCode?.state?.name;
  const header = <PageHeading page={pageHeading} />;
  const flags = useFlags();

  if (billableResult.loading) return <Loading header={header} />;

  const projectDetailProps = {
    location: getProjectDetailsCopy('location', {
      state: stateFullName,
    }),
    mdIdentifier: null,
    svgSrc: SolarPanels,
    title: getProjectDetailsCopy('title'),
    originationSourceType,
  };
  const billable = billableResult?.data?.plan?.billable;
  const personalEnergyProductionCopy =
    getPersonalEnergyProductionCopy('pending');

  const mobileGrid = (
    <Grid width="100%" gap="24px">
      <MobileGridCell>
        <Welcome
          state={stateFullName}
          originationSourceType={originationSourceType}
        >
          <EnrollmentTimeline
            status={EnrollmentStatus.Pending}
            stateAbbreviation={stateAbbreviation}
            stateName={stateName}
            sponsoredSolar={originationSourceType === SPONSORED_SOLAR}
          />
        </Welcome>
        {flags.lbRewardsEnabled &&
          showRewardsModule(userStatus, userStatusError, userStatusLoading) && (
            <UserRewards userStatus={userStatus} mobile={true} />
          )}
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px', top: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        {!advocacyCampaign && (
          <PersonalEnergyProduction
            margin={{ bottom: '24px', top: '24px' }}
            billable={billable}
            copy={personalEnergyProductionCopy}
            originationSourceType={originationSourceType}
          />
        )}
        <StateSolarFarms
          stateId={stateId}
          stateFullName={stateFullName}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
        />
        {advocacyCampaign && (
          <PersonalEnergyProduction
            billable={billable}
            copy={personalEnergyProductionCopy}
            originationSourceType={originationSourceType}
          />
        )}
        <ProjectDetails {...projectDetailProps} />
        <EnergyBreakdown
          zipCode={zipCode}
          originationSourceType={originationSourceType}
        />
      </MobileGridCell>
    </Grid>
  );

  const desktopGrid = (
    <Grid width="100%" gap="24px">
      <LeftColumnGridCell>
        <Welcome
          state={stateFullName}
          originationSourceType={originationSourceType}
        >
          <EnrollmentTimeline
            status={EnrollmentStatus.Pending}
            stateAbbreviation={stateAbbreviation}
            stateName={stateName}
            sponsoredSolar={originationSourceType === SPONSORED_SOLAR}
          />
        </Welcome>
        <StateSolarFarms
          stateId={stateId}
          stateFullName={stateFullName}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
        />
      </LeftColumnGridCell>
      <RightColumnGridCell>
        {flags.lbRewardsEnabled &&
          showRewardsModule(userStatus, userStatusError, userStatusLoading) && (
            <UserRewards userStatus={userStatus} />
          )}
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        <PersonalEnergyProduction
          billable={billable}
          copy={personalEnergyProductionCopy}
          originationSourceType={originationSourceType}
        />
        <ProjectDetails {...projectDetailProps} />
        <EnergyBreakdown
          zipCode={zipCode}
          originationSourceType={originationSourceType}
        />
      </RightColumnGridCell>
    </Grid>
  );

  return (
    <>
      {header}
      <ReconnectUtilityNotification />
      <SolarNotification />
      {screenIsSmall(width) ? mobileGrid : desktopGrid}
    </>
  );
};
